<template lang="pug">
  div
</template>
<script setup lang="ts">
/**
 * https://developer.zendesk.com/api-reference/widget-messaging/web/core/
 */

import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useMQ } from 'sisu/js/mq'
import { useCookieManagement } from '@/js/components/cookies/useCookieManagement'
import { CHAT_OVERLAY, useLiveChat } from '@/js/composables/useLiveChat'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useUIStore } from '@/js/stores/uiStore'
import { loadScript } from '@/js/helpers/scripts'
import { getCookie } from '@/js/services/Utils'
import { useRogue } from '@/js/services/Rogue'

type MessengerUser = {
  name: string,
  email?: string,
}

type MessengerCommand = 'open' | 'close' | 'show' | 'hide' | 'resetWidget'

// Zendesk's own cookie categories: 'all' is essential + functional + analytics,
// 'functional' drops analytics, 'none' stores nothing and hides the widget
type MessengerCookieRange = 'all' | 'functional' | 'none'

type ZendeskMessenger = {
  (target: 'messenger', command: MessengerCommand): void,
  (target: 'messenger', command: 'updateUser', metadata: MessengerUser): void,
  (target: 'messenger:set', command: 'userMetadata', metadata: MessengerUser): void,
  (target: 'messenger:set', command: 'cookies', range: MessengerCookieRange): void,
  (target: 'messenger:set', command: 'locale', locale: string): void,
  (target: 'messenger:on', event: 'unreadMessages', callback: (count: number) => void): void,
}

declare global {
  interface Window {
    zE?: ZendeskMessenger,
  }
}

// Drawers slide in from the right at up to 420px, which leaves less than the messaging
// window's width beside them here, so the widget is hidden on these breakpoints instead
// of slid over. A denylist, so a breakpoint added to sisu later still gets the shift.
const HIDE_BREAKPOINTS = ['mobile', 'tablet']
// Drawer.vue's default `width`, used when a drawer opens without reporting one
const DEFAULT_DRAWER_WIDTH = 420
// Zendesk keys the visitor's conversation with `ZD-` prefixed entries. Undocumented, but
// the only signal available before the widget boots that there is a thread to resume.
const CONVERSATION_KEY_PREFIX = 'ZD-'
// The codes we can produce, as they appear in Zendesk's public locale list:
// https://support.zendesk.com/api/v2/locales/public.json
// There is no bare `en` there - English is `en-US` - while the other languages we ship are
// listed bare. Order matters: the language fallback below takes the first match.
const ZENDESK_LOCALES = [
  'en-US',
  'en-CA',
  'en-GB',
  'en-AU',
  'de',
  'es',
  'fr',
  'fr-CA',
  'it',
  'nl',
]

const rogue = useRogue()
const customerStore = useCustomerStore()
const uiStore = useUIStore()
const mq = useMQ()
const { COOKIE_NAME, requiresCookieOptIn } = useCookieManagement()
const { chatKey } = useLiveChat()

let ready = false
let activated = false
let readyPromise: Promise<void> | undefined

function messenger(command: MessengerCommand): void {
  window.zE?.('messenger', command)
}

function cookieRange(): MessengerCookieRange {
  const preferences = getCookie(COOKIE_NAME)

  if (!preferences) {
    return requiresCookieOptIn.value ? 'functional' : 'all'
  }

  try {
    return JSON.parse(preferences).performance ? 'all' : 'functional'
  } catch (e) {
    rogue.log.warn('Could not read cookie preferences', e)

    return 'functional'
  }
}

function applyCookiePreference(): void {
  window.zE?.('messenger:set', 'cookies', cookieRange())
}

function zendeskLocale(): string {
  const { lang, locale } = rogue.l10n

  return ZENDESK_LOCALES.find(l => l.toLowerCase() === locale?.toLowerCase())
    || ZENDESK_LOCALES.find(l => l.startsWith(lang))
    || 'en-US'
}

function offsetWidget(width: number): void {
  const root = document.documentElement

  if (width > 0) {
    root.style.setProperty('--zendesk-drawer-offset', `${width}px`)
    root.classList.add('zendesk-drawer-open')

    return
  }

  root.classList.remove('zendesk-drawer-open')
  root.style.removeProperty('--zendesk-drawer-offset')
}

function syncWidgetToDrawer(): void {
  if (!ready || !activated) {
    return
  }

  if (!uiStore.drawer.drawerActive) {
    offsetWidget(0)
    messenger('show')

    return
  }

  if (HIDE_BREAKPOINTS.includes(mq.value)) {
    offsetWidget(0)
    messenger('hide')
  } else {
    offsetWidget(uiStore.drawer.drawerWidth || DEFAULT_DRAWER_WIDTH)
    messenger('show')
  }
}

function identify(): void {
  if (!customerStore.loggedIn || !window.zE) {
    return
  }

  const user: MessengerUser = { name: `${customerStore.firstname} ${customerStore.lastname}` }

  if (customerStore.email) {
    user.email = customerStore.email
  }

  try {
    window.zE('messenger:set', 'userMetadata', user)
    window.zE('messenger', 'updateUser', user)
  } catch (e) {
    rogue.log.info('Error identifying', e)
  }
}

async function loadZendesk(): Promise<void> {
  const key = chatKey.value

  if (!key) {
    return
  }

  try {
    await loadScript({
      script : `https://static.zdassets.com/ekr/snippet.js?key=${key}`,
      async  : true,
      id     : 'ze-snippet',
    }, rogue)
  } catch (e) {
    rogue.log.warn('Error loading Zendesk messaging widget', e)

    return
  }

  if (!window.zE) {
    rogue.log.warn('Zendesk messaging widget loaded without exposing zE')

    return
  }

  ready = true

  applyCookiePreference()

  window.zE('messenger:set', 'locale', zendeskLocale())

  messenger('hide')

  window.zE('messenger:on', 'unreadMessages', count => {
    if (count > 0) {
      activated = true
      syncWidgetToDrawer()
    }
  })
}

function whenIdle(callback: () => void): void {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => callback())

    return
  }

  window.setTimeout(callback, 3000)
}

function hasStoredConversation(): boolean {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      if (window.localStorage.key(i)?.startsWith(CONVERSATION_KEY_PREFIX)) {
        return true
      }
    }
  } catch (e) {
    rogue.log.info('Could not read Zendesk conversation state', e)
  }

  return false
}

async function openLiveChat(): Promise<void> {
  readyPromise ??= loadZendesk()
  await readyPromise

  if (!ready || !window.zE) {
    return
  }

  applyCookiePreference()
  identify()

  activated = true
  messenger('show')
  messenger('open')
}

watch([() => uiStore.drawer, mq], syncWidgetToDrawer)

watch(() => customerStore.loggedIn, (val, previous) => {
  if (!ready) {
    return
  }

  if (val) {
    identify()

    return
  }

  if (previous) {
    messenger('resetWidget')
    activated = false
    offsetWidget(0)
  }
})

watch(() => uiStore.overlay, val => {
  if (val === CHAT_OVERLAY) {
    rogue.log.info('Showing chat')
    openLiveChat()
    uiStore.hideOverlay()
  }
})

onMounted(() => {
  if (hasStoredConversation()) {
    whenIdle(() => {
      readyPromise ??= loadZendesk()
    })
  }
})

onBeforeUnmount(() => {
  offsetWidget(0)
})
</script>

<style lang="scss">
/**
 * The widget lives in iframes Zendesk appends to the body, so it can only be moved from
 * outside the component. `transform` rather than `right`, because Zendesk owns the inline
 * positioning on these iframes and rewrites it whenever the widget changes state.
 */
#launcher,
#webWidget,
iframe[title*="messaging" i] {
  // Unconditional, so the widget animates back as well as out - the transform below
  // would otherwise vanish in the same frame the drawer starts closing
  transition: transform 300ms;
}

html.zendesk-drawer-open {

  #launcher,
  #webWidget,
  iframe[title*="messaging" i] {
    transform: translateX(calc(var(--zendesk-drawer-offset, 0px) * -1)) !important;
  }
}
</style>
